import type { ReactNode } from 'react';
import { Button } from '@ksyos/design-system';
import classnames from 'classnames';
import { Fragment } from 'react';
import { Col } from 'react-bootstrap';
import { IconColor } from '../../atoms/icon/Icon';
import { CardBody } from '../cardBody/CardBody';
import { CardFooter } from '../cardFooter/CardFooter';
import { CardHeader } from '../cardHeader/CardHeader';
import { IconLink } from '../iconLink/IconLink';

export enum CardTheme {
    empty = 'empty',
    white = 'white-theme',
}

interface ICardProps {
    body: string;
    buttonsText?: string[];
    cardTitle: string;
    children?: ReactNode;
    className?: string;
    headerLarge?: boolean;
    icon?: string;
    linksText?: string[];
    linksTo?: string[];
    onClick?: () => void;
    openInNewTab?: boolean;
    position?: 'left' | 'right';
    style?: React.CSSProperties;
    theme?: CardTheme;
}

export function Card(props: Readonly<ICardProps>) {
    return (
        <div
            className={classnames('card', props.theme ? `theme-${props.theme}` : '', props.className ?? '')}
            style={props.style}
        >
            <CardHeader
                className={!props.body ? 'clearfix' : ''}
                isLarge={props.headerLarge}
            >
                {props.cardTitle}
            </CardHeader>
            <CardBody>{props.body}</CardBody>
            {props.children}
            {props.linksText &&
                (props.linksTo || props.onClick) &&
                props.linksText.map((linkText, index) => (
                    <CardFooter key={linkText}>
                        <IconLink
                            color={props.theme ? IconColor.black : IconColor.primary}
                            icon={props.icon ?? 'caret-right'}
                            linkColor={props.theme ? 'black' : 'primary'}
                            onClick={props.onClick}
                            openInNewTab={props.openInNewTab}
                            position={props.position ?? 'right'}
                            to={props.linksTo?.[index] ?? '#'}
                        >
                            {linkText}
                        </IconLink>
                    </CardFooter>
                ))}

            {props.buttonsText &&
                (props.linksTo || props.onClick) &&
                props.buttonsText.map((buttonText, index) => (
                    <Fragment key={buttonText}>
                        <Button
                            iconLeft={props.icon === 'download' ? props.icon : undefined}
                            onClick={() => {
                                if (props.onClick) {
                                    props.onClick();

                                    return;
                                }
                                window.location.href = props.linksTo?.[index] ?? '#';
                            }}
                            variant="solid"
                        >
                            {buttonText}
                        </Button>
                    </Fragment>
                ))}
        </div>
    );
}

interface ICardColumnProps extends ICardProps {
    lg?: number;
    mb?: number;
    md?: number;
    xs?: number;
}

export function CardColumn(props: Readonly<ICardColumnProps>) {
    const mbClassName = `mb-${props.mb ?? 3}`;

    return (
        <Col
            lg={props.lg}
            md={props.md}
            xs={props.xs}
        >
            <Card
                className={classnames(props.className, mbClassName)}
                {...props}
            />
        </Col>
    );
}

export function FixedCard(props: Readonly<Omit<ICardColumnProps, 'xs' | 'lg'>>) {
    return (
        <CardColumn
            md={props.md ?? 6}
            {...props}
        />
    );
}

export function ResponsiveCard(props: Readonly<Omit<ICardColumnProps, 'md'>>) {
    return (
        <CardColumn
            lg={props.lg ?? 6}
            xs={props.xs ?? 12}
            {...props}
        />
    );
}
